import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type OutputFormat = 'avif' | 'jpg' | 'png'
type SourceFormat = 'AVIF' | 'JPG' | 'PNG' | 'WebP'
type ConvertStatus = 'idle' | 'ready' | 'converting' | 'done' | 'error'
type AvifErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'unsupported_avif_decode'
  | 'unsupported_avif_encode'
  | 'corrupt_image'
  | 'canvas_error'
  | 'conversion_failed'

type AvifCopy = Translations['avifConverter']

interface AvifFileState {
  file: File
  sourceUrl: string
  sourceSize: number
  sourceFormat: SourceFormat
}

interface ConvertedState {
  blob: Blob
  url: string
  size: number
}

const UTILITY_KEY = 'avif-converter'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const ACCEPTED_TYPES = ['image/avif', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const ACCEPTED_EXTENSIONS = ['.avif', '.jpg', '.jpeg', '.png', '.webp']
const OUTPUT_MIME: Record<OutputFormat, 'image/avif' | 'image/jpeg' | 'image/png'> = {
  avif: 'image/avif',
  jpg: 'image/jpeg',
  png: 'image/png',
}

let avifEncodeSupport: Promise<boolean> | null = null

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function isSupportedImage(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

function sourceFormat(file: File): SourceFormat {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()

  if (type === 'image/avif' || name.endsWith('.avif')) return 'AVIF'
  if (type === 'image/png' || name.endsWith('.png')) return 'PNG'
  if (type === 'image/webp' || name.endsWith('.webp')) return 'WebP'
  return 'JPG'
}

function outputLabel(format: OutputFormat): 'AVIF' | 'JPG' | 'PNG' {
  if (format === 'avif') return 'AVIF'
  if (format === 'jpg') return 'JPG'
  return 'PNG'
}

function outputFilename(inputName: string, format: OutputFormat): string {
  const base = inputName.replace(/\.[^.]+$/, '') || 'converted'
  return `${base}.${format === 'jpg' ? 'jpg' : format}`
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

class AvifConverterError extends Error {
  constructor(
    message: string,
    readonly code: AvifErrorCode,
  ) {
    super(message)
  }
}

function validateFile(file: File, copy: AvifCopy): { message: string; code: AvifErrorCode } | null {
  if (!isSupportedImage(file)) {
    return { message: copy.errorsText.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_FILE_SIZE) {
    return { message: copy.errorsText.tooLarge, code: 'too_large' }
  }
  return null
}

function loadImage(fileState: AvifFileState, copy: AvifCopy): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => {
      const isAvif = fileState.sourceFormat === 'AVIF'
      reject(
        new AvifConverterError(
          isAvif ? copy.errorsText.unsupportedAvifDecode : copy.errorsText.corrupt,
          isAvif ? 'unsupported_avif_decode' : 'corrupt_image',
        ),
      )
    }
    image.src = fileState.sourceUrl
  })
}

function supportsAvifEncode(): Promise<boolean> {
  if (!avifEncodeSupport) {
    avifEncodeSupport = new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      if (!canvas.toBlob) {
        resolve(false)
        return
      }
      canvas.toBlob((blob) => resolve(blob?.type === 'image/avif'), 'image/avif', 0.8)
    })
  }
  return avifEncodeSupport
}

function encodeCanvas(
  canvas: HTMLCanvasElement,
  outputFormat: OutputFormat,
  quality: number,
  copy: AvifCopy,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new AvifConverterError(copy.errorsText.conversion, 'conversion_failed'))
          return
        }
        if (outputFormat === 'avif' && blob.type !== 'image/avif') {
          reject(
            new AvifConverterError(
              copy.errorsText.unsupportedAvifEncode,
              'unsupported_avif_encode',
            ),
          )
          return
        }
        resolve(blob)
      },
      OUTPUT_MIME[outputFormat],
      outputFormat === 'png' ? undefined : quality / 100,
    )
  })
}

async function convertImage(
  fileState: AvifFileState,
  outputFormat: OutputFormat,
  quality: number,
  copy: AvifCopy,
): Promise<Blob> {
  if (outputFormat === 'avif' && !(await supportsAvifEncode())) {
    throw new AvifConverterError(copy.errorsText.unsupportedAvifEncode, 'unsupported_avif_encode')
  }

  try {
    const image = await loadImage(fileState, copy)
    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight

    const context = canvas.getContext('2d')
    if (!context) {
      throw new AvifConverterError(copy.errorsText.canvas, 'canvas_error')
    }

    if (outputFormat === 'jpg') {
      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, canvas.width, canvas.height)
    }

    context.drawImage(image, 0, 0)
    return await encodeCanvas(canvas, outputFormat, quality, copy)
  } catch (error) {
    if (error instanceof AvifConverterError) {
      throw error
    }
    throw new AvifConverterError(copy.errorsText.conversion, 'conversion_failed')
  }
}

export function AvifConverterPage() {
  const t = useT()
  const copy = t.avifConverter
  const [fileState, setFileState] = useState<AvifFileState | null>(null)
  const [converted, setConverted] = useState<ConvertedState | null>(null)
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('avif')
  const [quality, setQuality] = useState(80)
  const [status, setStatus] = useState<ConvertStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileStateRef = useRef<AvifFileState | null>(null)
  const convertedRef = useRef<ConvertedState | null>(null)

  useEffect(() => {
    fileStateRef.current = fileState
  }, [fileState])

  useEffect(() => {
    convertedRef.current = converted
  }, [converted])

  useEffect(() => () => {
    if (fileStateRef.current) URL.revokeObjectURL(fileStateRef.current.sourceUrl)
    if (convertedRef.current) URL.revokeObjectURL(convertedRef.current.url)
  }, [])

  useEffect(() => {
    if (!converted) return
    URL.revokeObjectURL(converted.url)
    setConverted(null)
    setStatus('ready')
  }, [outputFormat, quality])

  const setFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file, copy)
      if (fileState) URL.revokeObjectURL(fileState.sourceUrl)
      if (converted) URL.revokeObjectURL(converted.url)
      setConverted(null)

      if (validationError) {
        toolError(UTILITY_KEY, validationError.code, { direction: 'unknown' })
        setFileState(null)
        setStatus('error')
        setError(validationError.message)
        return
      }

      setFileState({
        file,
        sourceUrl: URL.createObjectURL(file),
        sourceSize: file.size,
        sourceFormat: sourceFormat(file),
      })
      setStatus('ready')
      setError(null)
    },
    [converted, copy, fileState],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        setFile(file)
        event.target.value = ''
      }
    },
    [setFile],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      const file = event.dataTransfer.files[0]
      if (file) setFile(file)
    },
    [setFile],
  )

  const handleConvert = useCallback(async () => {
    if (!fileState || status === 'converting') return

    const direction = `${fileState.sourceFormat.toLowerCase()}-to-${outputFormat}`
    toolInvoke(UTILITY_KEY, { direction, format: outputFormat })
    setStatus('converting')
    setError(null)

    const startedAt = performance.now()
    try {
      const blob = await convertImage(fileState, outputFormat, quality, copy)
      if (converted) URL.revokeObjectURL(converted.url)
      const result = {
        blob,
        url: URL.createObjectURL(blob),
        size: blob.size,
      }
      setConverted(result)
      setStatus('done')
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startedAt), {
        direction,
        format: outputFormat,
      })
    } catch (caught) {
      const code = caught instanceof AvifConverterError ? caught.code : 'conversion_failed'
      toolError(UTILITY_KEY, code, { direction, format: outputFormat })
      setConverted(null)
      setStatus('error')
      setError(caught instanceof Error ? caught.message : copy.errorsText.generic)
    }
  }, [converted, copy, fileState, outputFormat, quality, status])

  const handleDownload = useCallback(() => {
    if (!converted || !fileState) return
    downloadBlob(converted.blob, outputFilename(fileState.file.name, outputFormat))
  }, [converted, fileState, outputFormat])

  const delta =
    fileState && converted
      ? Math.round(((converted.size - fileState.sourceSize) / fileState.sourceSize) * 100)
      : null
  const canConvert = Boolean(fileState) && status !== 'converting'
  const resultLabel = outputLabel(outputFormat)

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['AVIF, JPG, PNG, WebP', t.common.maxFileSize]}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="min-w-0 space-y-6">
          <Card
            className={cn('transition-colors', dropActive && 'border-primary bg-primary/5')}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                AVIF
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground">
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".avif,.jpg,.jpeg,.png,.webp,image/avif,image/jpeg,image/png,image/webp"
                disabled={status === 'converting'}
                className="hidden"
                onChange={handleInputChange}
              />
              <Button
                disabled={status === 'converting'}
                onClick={() => inputRef.current?.click()}
              >
                {fileState ? copy.replaceFile : copy.chooseFile}
              </Button>
            </CardContent>
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <CardContent className="p-0">
              <div className="grid grid-cols-2 gap-px bg-border">
                <div className="bg-muted">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                    <span>{copy.sourceLabel}</span>
                    <span>{fileState?.sourceFormat ?? 'FILE'}</span>
                  </div>
                  <div className="aspect-video bg-background/65">
                    {fileState ? (
                      <img
                        src={fileState.sourceUrl}
                        alt={fileState.file.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="grid h-full place-items-center text-sm text-muted-foreground">
                        {copy.waitingSource}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-muted">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                    <span>{copy.resultLabel}</span>
                    <span>{resultLabel}</span>
                  </div>
                  <div className="aspect-video bg-background/65">
                    {converted ? (
                      <img
                        src={converted.url}
                        alt={fileState ? outputFilename(fileState.file.name, outputFormat) : resultLabel}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="grid h-full place-items-center text-sm text-muted-foreground">
                        {copy.waitingResult}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-4">
                {fileState && (
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium" title={fileState.file.name}>
                      {fileState.file.name}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        {fileState.sourceFormat} {'->'} {resultLabel}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(fileState.sourceSize)}
                      </span>
                      {converted && (
                        <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                          {formatSize(converted.size)}
                          {delta !== null && (
                            <span
                              className={cn(
                                'ml-1 font-medium',
                                delta <= 0 ? 'text-primary' : 'text-muted-foreground',
                              )}
                            >
                              {delta > 0 ? '+' : ''}
                              {delta}%
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {status === 'converting' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {t.common.converting}
                  </div>
                )}

                {status === 'done' && converted && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-primary">{t.common.done}</span>
                    <Button size="sm" variant="outline" onClick={handleDownload}>
                      {t.common.download}
                    </Button>
                  </div>
                )}

                {status === 'error' && error && (
                  <div className="text-sm text-destructive">{error}</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
          <Card>
            <CardContent className="space-y-5 p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-normal">
                  {t.converter.settings}
                </h2>
                <LocalizedProcessingBadge variant="local" />
              </div>

              <div className="space-y-2">
                <span className="text-sm font-medium">{copy.outputFormat}</span>
                <div className="grid grid-cols-3 gap-2">
                  {(['avif', 'jpg', 'png'] as const).map((format) => (
                    <button
                      key={format}
                      type="button"
                      disabled={status === 'converting'}
                      onClick={() => setOutputFormat(format)}
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                        outputFormat === format
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {outputLabel(format)}
                    </button>
                  ))}
                </div>
              </div>

              {outputFormat !== 'png' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">
                      {outputFormat === 'avif' ? copy.avifQualityLabel : copy.jpegQualityLabel}
                    </span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {quality}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={40}
                    max={100}
                    step={1}
                    value={quality}
                    disabled={status === 'converting'}
                    onChange={(event) => setQuality(Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </div>
              )}

              <Button
                className="w-full"
                size="lg"
                onClick={handleConvert}
                disabled={!canConvert}
              >
                {status === 'converting' ? t.common.converting : t.common.convert}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-5">
              <h2 className="text-lg font-semibold tracking-normal">{t.converter.summary}</h2>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex justify-between gap-3">
                  <span>{t.converter.originalSize}</span>
                  <strong className="font-medium text-foreground">
                    {formatSize(fileState?.sourceSize ?? 0)}
                  </strong>
                </div>
                {converted && (
                  <div className="flex justify-between gap-3">
                    <span>{t.converter.convertedSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(converted.size)}
                    </strong>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
            {copy.privacyNote}
          </p>
        </aside>
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
